import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

// DPDP-compliant cookie consent banner.
// Records the user's decision in localStorage so we don't re-prompt.
// Accept and Reject are given equal visual weight, as required.

const STORAGE_KEY = "sl_cookie_consent"; // "accepted" | "rejected"

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const existing = window.localStorage.getItem(STORAGE_KEY);
      if (!existing) {
        // Small delay so it doesn't fight with initial page-load animations.
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch {
      // If localStorage is unavailable, still show the banner (fail-open to consent request).
      setVisible(true);
    }
  }, []);

  const decide = (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* ignore */
    }
    setVisible(false);
    // Broadcast for any analytics loaders that may listen later.
    window.dispatchEvent(new CustomEvent("sl:cookie-consent", { detail: choice }));
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-consent"
          data-testid="cookie-consent-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[60]"
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
        >
          <div className="relative rounded-2xl bg-[#1C1A17] text-white shadow-2xl shadow-black/30 border border-white/10 p-5 sm:p-6">
            <button
              type="button"
              onClick={() => decide("rejected")}
              aria-label="Dismiss and reject non-essential cookies"
              data-testid="cookie-consent-dismiss"
              className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-3 pr-6">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#B8935A]/20 text-[#B8935A]">
                <Cookie className="h-4 w-4" />
              </span>
              <div>
                <h3
                  id="cookie-consent-title"
                  className="font-head text-lg font-medium tracking-tight"
                >
                  We value your privacy
                </h3>
                <p
                  id="cookie-consent-desc"
                  className="mt-1.5 text-sm text-white/70 leading-relaxed"
                >
                  We use essential cookies to run this website and optional analytics
                  cookies to understand how it&apos;s used. See our{" "}
                  <Link
                    to="/privacy-policy"
                    data-testid="cookie-consent-privacy-link"
                    className="underline underline-offset-4 text-[#B8935A] hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => decide("rejected")}
                data-testid="cookie-consent-reject"
                className="flex-1 h-10 rounded-full border border-white/25 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => decide("accepted")}
                data-testid="cookie-consent-accept"
                className="flex-1 h-10 rounded-full bg-[#B8935A] text-white text-sm font-semibold hover:bg-[#9C7A44] transition-colors"
              >
                Accept
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
